import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, FileText, Trash2 } from "lucide-react";
import Header from "@/components/Header";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { CensoredText } from "@/lib/profanityFilter";

interface MyReview {
  id: string;
  title: string;
  organization: string;
  type: string;
  rating: number;
  created_at: string;
}

interface MyComment {
  id: string;
  content: string;
  created_at: string;
  review_id: string;
  review_title?: string;
}

const ActivityPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<MyReview[]>([]);
  const [comments, setComments] = useState<MyComment[]>([]);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  const fetchData = async () => {
    if (!user) return;
    const { data: rs } = await supabase
      .from("reviews_public" as any)
      .select("id, title, organization, type, rating, created_at")
      .eq("is_owner", true)
      .order("created_at", { ascending: false });
    setReviews((rs as any) || []);

    const { data: cs } = await supabase
      .from("review_comments_public" as any)
      .select("id, content, created_at, review_id")
      .eq("is_owner", true)
      .order("created_at", { ascending: false });
    const rows = (cs as any[]) || [];
    const reviewIds = [...new Set(rows.map((c) => c.review_id))];
    const titles: Record<string, string> = {};
    if (reviewIds.length > 0) {
      const { data: titleRows } = await supabase
        .from("reviews_public" as any)
        .select("id, title")
        .in("id", reviewIds);
      ((titleRows as any[]) || []).forEach((r) => {
        titles[r.id] = r.title;
      });
    }
    setComments(
      rows.map((c) => ({
        id: c.id,
        content: c.content,
        created_at: c.created_at,
        review_id: c.review_id,
        review_title: titles[c.review_id],
      }))
    );
  };

  useEffect(() => { fetchData(); }, [user]);

  const deleteComment = async (id: string) => {
    const { error } = await supabase.from("review_comments" as any).delete().eq("id", id);
    if (error) {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
      return;
    }
    fetchData();
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container max-w-md py-20 text-center text-muted-foreground">Loading...</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container max-w-2xl py-8">
        <motion.h1
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-serif text-3xl text-foreground mb-2"
        >
          Your Activity
        </motion.h1>
        <p className="text-sm text-muted-foreground mb-6">
          Reviews and comments you've posted. Reviews remain anonymous to other users.
        </p>

        <Tabs defaultValue="reviews">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="reviews" className="gap-1.5">
              <FileText className="w-4 h-4" /> Reviews ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="comments" className="gap-1.5">
              <MessageCircle className="w-4 h-4" /> Comments ({comments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reviews" className="space-y-3">
            {reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No reviews posted yet.</p>
            ) : (
              reviews.map((r) => (
                <Link
                  key={r.id}
                  to={`/review/${r.id}`}
                  className="block rounded-lg border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-muted/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-serif text-base text-card-foreground truncate">{r.title}</h3>
                      <p className="text-xs text-muted-foreground">{r.organization}</p>
                    </div>
                    <span className="text-xs font-bold text-primary shrink-0">{Number(r.rating).toFixed(1)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground uppercase tracking-wider">
                    <span>{r.type}</span>
                    <span>•</span>
                    <span>{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </Link>
              ))
            )}
          </TabsContent>

          <TabsContent value="comments" className="space-y-3">
            {comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No comments posted yet.</p>
            ) : (
              comments.map((c) => (
                <div key={c.id} className="rounded-lg border bg-card p-4 transition-colors hover:border-primary/50">
                  <Link to={`/review/${c.review_id}?comment=${c.id}`} className="block group">
                    <p className="text-xs text-muted-foreground mb-1">
                      On <span className="font-medium text-foreground group-hover:underline">{c.review_title || "review"}</span>
                    </p>
                    <p className="text-sm text-foreground whitespace-pre-wrap break-words"><CensoredText text={c.content} /></p>
                  </Link>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(c.created_at).toLocaleString()}
                    </span>
                    <button
                      onClick={() => deleteComment(c.id)}
                      className="text-[10px] text-destructive hover:text-destructive/80 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ActivityPage;
